import { type Root } from 'react-dom/client';
import { decodeToObject } from './utils';
import {
    type TRenderReactApp as TRenderReactApp,
    type TUpdateReactApp as TUpdateReactApp,
} from './renderer';
import config from '../../mfe.config';

const {
    customElement: { attributes, mountPoints },
} = config;

export type TFabricComponentInstance = InstanceType<typeof FabricComponent>;
export type TOnRenderedCallback = (instance: TFabricComponentInstance) => void;
export class FabricComponent extends HTMLElement {
    // React Root
    #app: Root | null = null;

    get #moduleName(): string | null {
        return this.getAttribute(attributes.moduleName);
    }
    // Base64 encoded OR string JSON
    get #moduleProps(): Record<string, string> {
        return (
            decodeToObject(this.getAttribute(attributes.params))?.params ?? {}
        );
    }

    get #moduleHydrate() {
        return this.getAttribute(attributes.hydrate);
    }

    observer: IntersectionObserver | undefined;
    #renderReactApp: TRenderReactApp;
    #updateReactApp: TUpdateReactApp;
    #onRenderedCallback?: TOnRenderedCallback;
    #forceHydrationMfes?: Array<string> = [];

    constructor(
        renderReactApp: TRenderReactApp,
        updateReactApp: TUpdateReactApp,
        onRenderedCallback?: TOnRenderedCallback,
        forceHydrationMfes?: Array<string>
    ) {
        super();
        this.#renderReactApp = renderReactApp;
        this.#updateReactApp = updateReactApp;
        this.#onRenderedCallback = onRenderedCallback;
        this.#forceHydrationMfes = forceHydrationMfes;
    }

    connectedCallback() {
        if (this.#forceHydrationMfes?.includes(this.#moduleName ?? '')) {
            this.#renderApp();
        } else if (this.#moduleHydrate === 'false') {
            // render on interactions
            this.#renderOnInteraction();
        } else {
            // render when in view
            this.#renderOnInView();
        }

        this.dispatchEvent(new Event('micro-frontend-attached'));
    }

    disconnectedCallback() {
        this.#destroy();
    }

    #renderOnInView() {
        this.observer = new IntersectionObserver(this.#onInView.bind(this));
        this.observer.observe(this);
    }

    #renderOnInteraction() {
        // render on clicks
        const clickElements =
            this.#getRootMountpoint().querySelectorAll<HTMLElement>(
                '[data-hydrate="click"]'
            );
        clickElements.forEach(element => {
            const clickHandler = () => {
                if (this.#app) {
                    element.removeEventListener('click', clickHandler);
                    return;
                }
                this.#renderApp();
                element.removeEventListener('click', clickHandler);
                setTimeout(() => {
                    element.click();
                }, 1000);
            };
            element.addEventListener('click', clickHandler);
        });

        //render on mouseover
        const mouseoverElements =
            this.#getRootMountpoint().querySelectorAll<HTMLElement>(
                '[data-hydrate="mouseover"]'
            );
        mouseoverElements.forEach(element => {
            const mouseoverHandler = () => {
                if (this.#app) {
                    element.removeEventListener('mouseover', mouseoverHandler);
                    return;
                }
                this.#renderApp();
                element.removeEventListener('mouseover', mouseoverHandler);
            };
            element.addEventListener('mouseover', mouseoverHandler);
        });
    }

    #onInView(entries: IntersectionObserverEntry[]) {
        if (this.#app) {
            this.observer?.disconnect();
            this.observer = undefined;
            return;
        }
        for (const entry of entries) {
            if (entry.isIntersecting) {
                this.observer?.disconnect();
                this.observer = undefined;

                this.#renderApp();
                return;
            }
        }
    }

    #renderApp() {
        if (!this.#moduleName) {
            this.#destroy();
            return;
        }

        if (!this.#app) {
            const state = this.#getAppState();
            this.#app = this.#renderReactApp(
                this.#getRootMountpoint(),
                this.#moduleName,
                this.#moduleProps,
                state
            );
        } else
            this.#updateReactApp(
                this.#app,
                this.#moduleName,
                this.#moduleProps
            );

        try {
            this.#onRenderedCallback?.(this);
        } catch (e) {
            console.error(e);
        }
    }

    #destroy() {
        this.#app?.unmount();
        this.#app = null;
    }

    #getRootMountpoint() {
        const rootMountPoint = this.querySelector<HTMLElement>(
            `[${mountPoints.root}]`
        );
        if (!rootMountPoint) {
            const rootEl = document.createElement('div');
            rootEl.setAttribute(mountPoints.root, '');
            rootEl.setAttribute('style', 'display: contents;');
            this.appendChild(rootEl);
            return rootEl;
        }
        return rootMountPoint;
    }

    #getAppState() {
        const stateMountPoint = this.querySelector<HTMLElement>(
            `[${mountPoints.state}]`
        );
        if (!stateMountPoint || !stateMountPoint.textContent) return;
        try {
            const state = decodeToObject(stateMountPoint.textContent);
            stateMountPoint.remove();
            return state;
        } catch (e) {
            return undefined;
        }
    }
}
